// =============================================================================
// components/Boot.tsx — authenticated-session boot orchestration (bare-RN).
//
// Bare-RN port of the cross-cutting BOOT EFFECTS that lived in the Expo app's
// app/_layout.tsx. App.tsx mounts <Boot /> exactly once, ABOVE the navigator,
// ONLY while the auth gate reports state==='authed' (see App.tsx). It renders
// NOTHING — it is a side-effect host, like InAppNotification / IncomingCallHandler.
//
// WHY a dedicated component (not inline in App.tsx): the effects below must run
// with a VALID auth session and tear down on logout. Gating <Boot /> on
// state==='authed' means React mounts it on login and unmounts it on logout, so
// every effect's cleanup fires automatically — no manual isAuthed guards needed
// inside each effect, and no stale listeners after sign-out.
//
// WIRED HERE (each ported from _layout.tsx; see inline notes):
//   1. SOCKET CONNECT ON AUTH — connectSocket() so realtime (notifications, DMs,
//      follow/feed events) is live app-wide BEFORE any chat/feed screen opens.
//      Do NOT rely on the lazy connect inside joinChannel/onDmMessage.
//   2. A15 BLOCK REGISTRY — loadBlockedUsers() (device-local) then a server
//      /settings/blocked pull → syncBlockedUsers() so blocked content hides
//      everywhere from the first feed render.
//   3. LR4 SOCIAL GRAPH — startSocialGraph() for live follow counts + reverse
//      block-hide via the LR1 socket spine. Idempotent.
//   4. LR9 APPSTATE FOREGROUND RECONCILE — on return-to-foreground: reconnect the
//      socket + fetchUnreadCounts() → reconcileDmUnread() so the nav DM/notif
//      badges converge to server truth after a background trip. ADDITIVE to the
//      per-screen onReconnect handlers; useAuth ALSO re-validates the session on
//      foreground (different concern — gate vs. badges — so no duplication).
//   5. PUSH — registerForPush() (P4 STUB; no-ops safely today). Wiring in place so
//      P4's real notifee/FCM token source needs no new call site.
//   6. PUSH-TAP COLD-START / DEEP LINK — react-navigation's `linking` config
//      already routes cryb:// URLs + a notification whose payload carries a
//      `url`/`actionUrl` relative path. The native notification-RESPONSE listener
//      (tap a push whose payload is type-based, e.g. {type:'dm',conversationId})
//      is P4 (no expo-notifications in bare RN yet). We install a tolerant,
//      no-throw bridge: if a notifee/native module is present it routes the tap;
//      if not (today) it cleanly no-ops. See routeFromPushData below.
// =============================================================================
import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  AppState,
  Linking,
  StyleSheet,
  View,
  type AppStateStatus,
} from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  runOnJS,
  Easing,
} from 'react-native-reanimated';
import SplashScreen from './SplashScreen';
import { animationTokens, ThemeMode } from '../constants/theme';
import { api } from '../services/api';
import { loadBlockedUsers, syncBlockedUsers, loadMutedUsers, syncMutedUsers } from '../services/moderation';
import { registerForPush } from '../services/push';
import { startSocialGraph } from '../services/socialGraph';
import { connectSocket, fetchUnreadCounts } from '../services/socket';
import { startPresence, stopPresence } from '../services/presence';
import { reconcileDmUnread } from '../services/dmState';
import { navigate } from '../navigation/navigationRef';
import type { RootStackParamList } from '../navigation/types';

// ───────────────────────────────────────────────────────────────────────────
// PUSH-TAP ROUTING (LR7 parity) — resolve a push `data` payload to an in-app
// navigate() call. Mirrors the _layout.tsx type→route map, but targets the
// react-navigation route names (navigation/types.ts) instead of expo-router
// paths. Returns true if it navigated. SECURITY: only navigates to known route
// names with validated params — a spoofed payload can't open-redirect.
//
// The native RESPONSE listener that feeds this (tap → here) is P4 (no
// expo-notifications in bare RN yet). routeFromPushData is exported so P4's
// notifee/FCM `onNotificationOpenedApp` + `getInitialNotification` (cold start)
// can call it directly with no new logic. Today it's reachable only via the
// optional native-module probe in the effect below, which no-ops when absent.
// ───────────────────────────────────────────────────────────────────────────
export function routeFromPushData(data: any): boolean {
  try {
    if (!data || typeof data !== 'object') return false;

    // 0. CALL (LR8): a backgrounded/killed incoming-call push → the full-screen
    //    call screen WITH params so it auto-joins (tapping the push = accepting).
    if (String(data.type || '').toLowerCase() === 'call' && data.callId) {
      navigate('VoiceCall', {
        callId: String(data.callId),
        autoJoin: true,
        media: data.media === 'audio' ? 'audio' : 'video',
        roomName: data.roomName ? String(data.roomName) : '',
        peerName: data.callerName ? String(data.callerName) : 'Call',
        peerAvatar: data.callerAvatar ? String(data.callerAvatar) : '',
        isCaller: false,
      });
      return true;
    }

    // 1. explicit relative path (social call sites send `url`; the
    //    NotificationService path sends `actionUrl`). Parse the leading segment
    //    and map it onto a typed route. Reject absolute/scheme/`..` paths.
    const explicit = data.url ?? data.actionUrl;
    if (typeof explicit === 'string' && explicit.length > 0) {
      if (routeFromPath(explicit)) return true;
    }

    // 2. derive from data.type (+ ids) — the type→route map.
    return routeFromType(data);
  } catch {
    return false;
  }
}

// Map a relative in-app path (e.g. '/post/123', '/dm/abc', '/user/jane') to a
// typed navigate(). Rejects anything that isn't a safe relative app route.
function routeFromPath(raw: string): boolean {
  if (typeof raw !== 'string' || raw.length === 0) return false;
  if (/^[a-z]+:/i.test(raw) || raw.startsWith('//') || raw.includes('..')) return false;
  if (!raw.startsWith('/')) return false;
  const seg = raw.replace(/^\/+/, '').split(/[/?#]/);
  const head = seg[0];
  const id = seg[1] ? decodeURIComponent(seg[1]) : '';
  switch (head) {
    case 'post':            return id ? nav('Post', { id }) : false;
    case 'community':       return id ? nav('Community', { name: id }) : nav('Communities', undefined);
    case 'communities':     return nav('Communities', undefined);
    case 'user':            return id ? nav('UserProfile', { username: id }) : false;
    case 'dm':              return id && id !== 'new' ? nav('DM', { id }) : nav('DMNew', undefined);
    case 'channel':         return id ? nav('Channel', { id }) : false;
    case 'notifications':   return nav('Notifications', undefined);
    case 'wallet':          return nav('Wallet', undefined);
    case 'bookmarks':       return nav('Bookmarks', undefined);
    case 'search':          return nav('Search', undefined);
    case 'learn':           return id ? nav('LearnArticle', { slug: id }) : nav('LearnIndex', undefined);
    case 'nft':             return id ? nav('NftDetail', { id }) : nav('NftMarketplace', undefined);
    case 'voice':           return id ? nav('VoiceRoom', { id }) : nav('VoiceRooms', undefined);
    default:                return false;
  }
}

// Map a push `data.type` (+ ids) to a typed navigate(). Returns false for an
// unknown type or missing required ids (caller falls through to a no-op).
function routeFromType(data: any): boolean {
  const type = String(data?.type || '').toLowerCase();
  switch (type) {
    case 'dm':
    case 'message': {
      const id = data?.conversationId ?? data?.channelId ?? data?.senderId;
      return id ? nav('DM', { id: String(id) }) : nav('Tabs', { screen: 'Chat' } as any);
    }
    case 'post_like':
    case 'like':
    case 'comment':
    case 'reply':
    case 'mention': {
      const id = data?.postId ?? data?.entityId;
      return id ? nav('Post', { id: String(id) }) : nav('Notifications', undefined);
    }
    case 'follow':
    case 'friend_request': {
      const u = data?.username ?? data?.actorUsername;
      return u ? nav('UserProfile', { username: String(u) }) : nav('Notifications', undefined);
    }
    case 'community':
    case 'community_post':
    case 'server_invite': {
      const c = data?.communityName ?? data?.communitySlug ?? data?.serverId;
      return c ? nav('Community', { name: String(c) }) : nav('Communities', undefined);
    }
    default:
      return false;
  }
}

// Tiny typed-navigate wrapper that always returns true so the route* maps read
// as expression switches. navigate() itself is no-throw + nav-ready-guarded.
function nav<R extends keyof RootStackParamList>(route: R, params: RootStackParamList[R]): boolean {
  navigate(route as any, params as any);
  return true;
}

// ───────────────────────────────────────────────────────────────────────────
// NATIVE → JS PUSH BRIDGE (cryb://internal/push?data=<json>)
//
// The native AppDelegate (PushKit/CallKit + UNUserNotificationCenter) has no
// custom RN module, so it hands a notification TAP or a CallKit ANSWER to JS by
// emitting an internal Linking URL whose `data` query param is the push payload.
// We parse it and reuse routeFromPushData (the SAME type→route map as a push
// tap), so a CallKit answer lands on VoiceCall (autoJoin) and a notif tap routes
// to its target. De-duped by callId because the native side retries the emit a
// few times to win the cold-start race (JS may not be listening yet).
// ───────────────────────────────────────────────────────────────────────────
const handledRecently = new Map<string, number>();
function alreadyHandled(key: string): boolean {
  const now = Date.now();
  // Drop stale keys so the map can't grow unbounded.
  for (const [k, t] of handledRecently) if (now - t > 30000) handledRecently.delete(k);
  if (handledRecently.has(key)) return true;
  handledRecently.set(key, now);
  return false;
}

function handleInternalPushUrl(url: string | null): boolean {
  try {
    if (!url || !url.startsWith('cryb://internal/push')) return false;
    // Manual parse (RN's URLSearchParams polyfill mangles percent-encoded JSON):
    // the URL is cryb://internal/push?data=<percent-encoded json>.
    const marker = 'data=';
    const at = url.indexOf(marker);
    if (at < 0) return false;
    const raw = url.slice(at + marker.length);
    if (!raw) return false;
    const data = JSON.parse(decodeURIComponent(raw));
    // De-dupe: a call by callId, everything else by a stable signature.
    const key = data?.callId ? `call:${data.callId}` : `${data?.type ?? 'evt'}:${data?.id ?? raw.slice(0, 64)}`;
    if (alreadyHandled(key)) return true;
    return routeFromPushData(data);
  } catch {
    return false;
  }
}

// ───────────────────────────────────────────────────────────────────────────
// P0-B — SPLASH GATE (single controlled render path; kills the "funky flash").
//
// THE PROBLEM this solves:
//   native launch storyboard  →  [intermediate unstyled frame]  →  JS app.
// On bare RN the native SplashScreen.storyboard shows until the JS root view
// paints its FIRST frame. If that first frame is `null` (App.tsx rendered
// nothing while the auth gate was 'loading') or a half-built navigator, the user
// sees a blank/unstyled flash before the real UI. That is the flash.
//
// THE FIX — one render tree, never a conditional swap:
//   <SplashGate ready={...}>{appTree}</SplashGate>
// The gate ALWAYS renders its children (the app tree mounts immediately, behind
// the cover), and lays the branded JS <SplashScreen/> OVER them as an absolute
// overlay. So the very first frame RN paints is the branded splash — a seamless
// visual continuation of the native storyboard, with NO blank frame between
// storyboard → JS → app. When `ready` flips true AND the splash's own entrance
// animation has settled, the overlay fades out (animationTokens.duration.slow)
// and then unmounts, revealing the already-warm app underneath. No tree swap,
// no re-mount, no unstyled intermediate frame.
//
// `ready` is the ONLY control signal the caller supplies — pass
// `authState !== 'loading'` (App.tsx owns the gate). A minimum on-screen time
// (minShowMs) avoids a jarring 1-frame blink when auth resolves synchronously.
//
// Additive + backward-compatible: a NEW export; nothing here changes Boot's
// default export or its props. Uses only P0-A primitives + animationTokens.
// ───────────────────────────────────────────────────────────────────────────
export interface SplashGateProps {
  /** True once the app is ready to show (e.g. auth gate left 'loading'). */
  ready: boolean;
  /** Splash theme. 'light' (default) or 'dark'. */
  theme?: ThemeMode;
  /** Minimum time (ms) the splash stays visible to avoid a 1-frame blink. */
  minShowMs?: number;
  /** The app tree. Always rendered (mounts warm behind the splash overlay). */
  children: React.ReactNode;
}

export function SplashGate({
  ready,
  theme = 'light',
  minShowMs = 600,
  children,
}: SplashGateProps) {
  // The overlay lives until ALL THREE gates clear (each is a boolean state so a
  // single reveal effect can depend on them and fire EXACTLY once):
  //   1. `ready`     — auth gate resolved (caller passes authState !== 'loading')
  //   2. `entered`   — the splash entrance animation settled (onReady)
  //   3. `minShown`  — the minimum on-screen time elapsed (anti-blink)
  const [visible, setVisible] = useState(true);
  const [entered, setEntered] = useState(false);
  const [minShown, setMinShown] = useState(minShowMs <= 0);
  const revealed = useRef(false);
  const cover = useSharedValue(1); // 1 = fully covering, 0 = revealed

  // Min-show timer: starts on mount so a fast/synchronous auth resolve still
  // shows a beat of the branded splash rather than blinking straight to the app.
  useEffect(() => {
    if (minShowMs <= 0) return;
    const id = setTimeout(() => setMinShown(true), minShowMs);
    return () => clearTimeout(id);
  }, [minShowMs]);

  const onEntered = useCallback(() => setEntered(true), []);
  const hide = useCallback(() => setVisible(false), []);

  // Reveal when ready + entered + min-show elapsed. Fade the cover on the UI
  // thread, then unmount the overlay (runOnJS) so it costs nothing afterwards.
  // `revealed` guards the timing driver so it can only ever start once.
  useEffect(() => {
    if (!visible || revealed.current) return;
    if (!ready || !entered || !minShown) return;
    revealed.current = true;
    cover.value = withTiming(
      0,
      { duration: animationTokens.duration.slow, easing: Easing.out(Easing.cubic) },
      (finished) => {
        'worklet';
        if (finished) runOnJS(hide)();
      },
    );
  }, [ready, entered, minShown, visible, cover, hide]);

  const coverStyle = useAnimatedStyle(() => ({ opacity: cover.value }));

  return (
    <View style={styles.gateRoot}>
      {/* App tree — ALWAYS rendered so it mounts/warms behind the cover. */}
      {children}
      {/* Branded splash overlay — the first painted frame; fades out on reveal. */}
      {visible && (
        <Animated.View
          style={[StyleSheet.absoluteFill, styles.gateOverlay, coverStyle]}
          pointerEvents={ready ? 'none' : 'auto'}
        >
          <SplashScreen theme={theme} onReady={onEntered} />
        </Animated.View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  gateRoot: { flex: 1 },
  gateOverlay: { zIndex: 1000 },
});

export default function Boot() {
  // registerForPush + startSocialGraph are session-once; guard against a remount
  // (e.g. a transient gate flip) re-firing them. They're both idempotent anyway,
  // but this keeps the boot log clean and avoids redundant work.
  const bootedOnce = useRef(false);

  // ── 1+2+3+5: connect socket, hydrate block registry, social graph, push ──────
  useEffect(() => {
    let active = true;

    // 1. SOCKET: bring realtime up immediately for the authed session so
    //    notifications/DMs/follow events flow app-wide before any screen opens.
    connectSocket().catch(() => {});

    // 1b. PRESENCE: announce this user as online for the session and start the
    //    AppState away/active toggling + the global presence listener, so peers
    //    see us online from login (not only once we open a presence-aware screen).
    //    The service is self-starting/idempotent; this just guarantees session-wide
    //    own-presence. Server marks us offline automatically on socket disconnect.
    //    Guarded: a synchronous throw here runs INSIDE a useEffect and would escape
    //    the render-only ErrorBoundary → hard crash right after login. Non-critical.
    try { startPresence(); } catch {}

    // 2. A15 BLOCK REGISTRY: device-local first (hides blocked content before the
    //    first feed paint), then reconcile against the server's block list.
    loadBlockedUsers().catch(() => {});
    (async () => {
      try {
        const res = await api<any>('/settings/blocked');
        if (!active || !res.success) return;
        const list = Array.isArray(res.data) ? res.data : [];
        await syncBlockedUsers(
          list
            .filter((b: any) => b && b.user)
            .map((b: any) => ({ id: b.user.id, username: b.user.username })),
        );
      } catch {
        // best-effort — device-local registry remains the fallback.
      }
    })();

    // MUTE REGISTRY (mirrors block): device-local first so muted content hides
    // before the first paint, then reconcile against the server's mute list.
    loadMutedUsers().catch(() => {});
    (async () => {
      try {
        const res = await api<any>('/settings/muted');
        if (!active || !res.success) return;
        const list = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.items) ? res.data.items : []);
        await syncMutedUsers(
          list
            .map((m: any) => (m?.user ? { id: m.user.id, username: m.user.username } : { id: m?.id, username: m?.username }))
            .filter((m: any) => m && (m.id || m.username)),
        );
      } catch {
        // best-effort — device-local registry remains the fallback.
      }
    })();

    if (!bootedOnce.current) {
      bootedOnce.current = true;
      // 5. PUSH (P4 stub today; no-ops safely). Fire-and-forget, never throws.
      // Guarded belt-and-braces: an effect-scope throw escapes the ErrorBoundary.
      try { registerForPush(); } catch {}
      // 3. LR4 SOCIAL GRAPH: live follow counts + reverse block-hide. Idempotent.
      try { startSocialGraph(); } catch {}
    }

    return () => {
      active = false;
      // Tear presence listeners down on logout (Boot unmounts when the auth gate
      // leaves 'authed'), so no stale AppState/socket presence handlers linger.
      stopPresence();
    };
  }, []);

  // ── 4: LR9 AppState foreground reconcile (socket up + badge convergence) ─────
  useEffect(() => {
    const reconcileOnForeground = () => {
      // Re-establish the socket (idempotent; no-op if already connected) so live
      // delivery resumes immediately rather than waiting for the next emit. The
      // socket service has its OWN foreground reconnect too; this is belt-and-
      // braces and harmless (connectSocket is fully idempotent).
      connectSocket().catch(() => {});
      // Reconcile the unread badges against the server (authoritative). Cheap:
      // one /sync/unread-counts call, no message bodies. Best-effort.
      fetchUnreadCounts()
        .then((counts) => {
          if (counts) reconcileDmUnread(counts.dmByConversation);
        })
        .catch(() => {});
    };

    const sub = AppState.addEventListener('change', (next: AppStateStatus) => {
      if (next === 'active') reconcileOnForeground();
    });
    return () => {
      try { sub.remove(); } catch {}
    };
  }, []);

  // ── 6: PUSH-TAP COLD-START / DEEP LINK (P4-ready, no-throw today) ────────────
  // react-navigation's `linking` config already handles cryb:// deep links and a
  // push whose payload carries a relative `url`/`actionUrl` path. What's NOT yet
  // wired (because bare RN has no expo-notifications) is the TYPE-based push tap
  // (e.g. {type:'dm',conversationId}) and the COLD-START path (app launched BY a
  // push tap). We probe for an OPTIONAL native notification module; if present we
  // wire tap → routeFromPushData and read the initial (cold-start) notification.
  // If absent (today), this cleanly no-ops — no crash, wiring ready for P4.
  useEffect(() => {
    let unsub: undefined | (() => void);
    (async () => {
      try {
        // P4 swaps this require for the real notifee/FCM module. Guarded so a
        // missing module never throws — the whole block is a no-op until then.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const notifee: any = (() => {
          try { return require('@notifee/react-native'); } catch { return null; }
        })();
        if (!notifee) return; // P4 not wired yet → graceful no-op.

        // Foreground/background tap → route.
        if (typeof notifee.onForegroundEvent === 'function') {
          unsub = notifee.onForegroundEvent((event: any) => {
            // EventType.PRESS === 1 in notifee; tolerate either the enum or the int.
            const isPress = event?.type === 1 || event?.type === 'PRESS';
            if (isPress) routeFromPushData(event?.detail?.notification?.data);
          });
        }
        // Cold-start: app launched by tapping a push (killed state).
        if (typeof notifee.getInitialNotification === 'function') {
          const initial = await notifee.getInitialNotification();
          const data = initial?.notification?.data;
          if (data) routeFromPushData(data);
        }
      } catch {
        // notifications module unavailable in this context — no-op.
      }
    })();
    return () => {
      try { unsub?.(); } catch {}
    };
  }, []);

  // ── 7: NATIVE PUSH/CALL BRIDGE (cryb://internal/push) ────────────────────────
  // The native AppDelegate (PushKit/CallKit + UNUserNotificationCenter) emits an
  // internal Linking URL on a notification TAP or a CallKit ANSWER. We route it
  // via routeFromPushData (CallKit answer → VoiceCall autoJoin; tap → target).
  // Also drains the cold-start URL (app launched by answering/tapping while killed).
  useEffect(() => {
    const sub = Linking.addEventListener('url', ({ url }) => { handleInternalPushUrl(url); });
    Linking.getInitialURL()
      .then((url) => { handleInternalPushUrl(url); })
      .catch(() => {});
    return () => {
      try { sub.remove(); } catch {}
    };
  }, []);

  // Side-effect-only host: renders nothing.
  return null;
}
